import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import DataTable from "react-data-table-component";
import "react-data-table-component-extensions/dist/index.css";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useModalContext } from "../../contexts/ModalContext";
import { useAuth } from "../../hooks/useAuth";
import * as ActivitiesService from "../../services/activities";
import * as MechanicActivityOffersService from "../../services/mechanicActivityOffers";
import ConfirmationModal from "../../components/ui/ConfirmationModal";
import OfferFormModal from "./OfferFormModal";
import { Container } from "./styles";

var moment = require("moment");

function parseApiError(error) {
    const d = error?.response?.data;
    if (typeof d === "string") return d;
    if (d?.message) return d.message;
    if (Array.isArray(d?.errors)) return d.errors.map((e) => e.defaultMessage || e.message).filter(Boolean).join(" ");
    return "Operação não pôde ser concluída.";
}

const STATUS_META = {
    PENDING: { label: "Pendente de aprovação", color: "info" },
    APPROVED: { label: "Aprovada", color: "success" },
    DENIED: { label: "Negada", color: "error" },
};

const MechanicActivityOffers = () => {
    const { setModal } = useModalContext();
    const { loggedMechanic } = useAuth();
    const [offers, setOffers] = useState([]);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deleteModal, setDeleteModal] = useState({ open: false, offer: null });

    const loadData = useCallback(async () => {
        if (!loggedMechanic?.id) return;
        setLoading(true);
        try {
            const [mine, acts] = await Promise.all([
                MechanicActivityOffersService.listMine(),
                ActivitiesService.getActivities(),
            ]);
            setOffers(Array.isArray(mine) ? mine : []);
            setActivities(Array.isArray(acts) ? acts : []);
        } catch (e) {
            console.error(e);
            alert(parseApiError(e));
            setOffers([]);
        } finally {
            setLoading(false);
        }
    }, [loggedMechanic?.id]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const occupiedActivityIds = useMemo(() => new Set(offers.map((o) => o.activityId)), [offers]);

    const openCreate = () =>
        setModal(
            <OfferFormModal offer={null} activities={activities} occupiedActivityIds={occupiedActivityIds} onSaved={loadData} />
        );

    const openEdit = (row) =>
        setModal(
            <OfferFormModal offer={row} activities={activities} occupiedActivityIds={occupiedActivityIds} onSaved={loadData} />
        );

    const handleDelete = async () => {
        const offer = deleteModal.offer;
        if (!offer?.id) return;
        try {
            await MechanicActivityOffersService.deleteOffer(offer.id);
            alert("Oferta removida com sucesso.");
            setDeleteModal({ open: false, offer: null });
            loadData();
        } catch (e) {
            alert(parseApiError(e));
        }
    };

    const columns = [
        {
            name: "Atividade",
            selector: (row) => row.activityName,
            sortable: true,
            grow: 1,
        },
        {
            name: "Desconto",
            center: true,
            selector: (row) => `${row.discountPercent}%`,
        },
        {
            name: "Válida até",
            selector: (row) => (row.validUntil ? moment(row.validUntil).format("DD/MM/YYYY") : "—"),
        },
        {
            name: "Status",
            center: true,
            cell: (row) => {
                const meta = STATUS_META[row.status] || { label: row.status || "—", color: "default" };
                return <Chip label={meta.label} size="small" color={meta.color} variant={meta.color === "default" ? "outlined" : "filled"} />;
            },
        },
        {
            name: "Motivo",
            selector: (row) => row.reason || "—",
            wrap: true,
        },
        {
            name: "Ações",
            minWidth: "160px",
            cell: (row) => (
                <Box display="flex" gap={0.5}>
                    <Button variant="outlined" size="small" onClick={() => openEdit(row)} aria-label="Editar oferta">
                        <EditIcon fontSize="small" />
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => setDeleteModal({ open: true, offer: row })}
                        aria-label="Excluir oferta"
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </Button>
                </Box>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    return (
        <Container>
            <Box
                mb={3}
                fontWeight="bold"
                color="#666666"
                fontSize={{
                    xs: "h7.fontSize",
                    sm: "h6.fontSize",
                    md: "h5.fontSize",
                }}
            >
                Gerenciar — Ofertas de atividades
            </Box>
            <Typography variant="body2" color="text.secondary" mb={2} maxWidth={720}>
                Cadastre descontos por atividade do catálogo. Cada estabelecimento pode ter uma oferta por atividade; ao salvar de novo
                para a mesma atividade, a oferta é atualizada e volta para análise (pendente). Somente ofertas aprovadas com data válida
                aparecem para clientes no app.
            </Typography>
            <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Button variant="contained" onClick={openCreate} startIcon={<AddIcon />}>
                    Nova oferta
                </Button>
                <Typography color="#a18e38" fontSize={{ xs: "h7.fontSize", sm: "h6.fontSize", md: "h6.fontSize" }}>
                    Total: <b>{offers.length}</b> {offers.length === 1 ? "oferta" : "ofertas"}
                </Typography>
            </Box>
            <Box mt={3} mb={5}>
                <Card>
                    <DataTable columns={columns} data={offers} progressPending={loading} pagination persistTableHead />
                </Card>
            </Box>
            <ConfirmationModal
                open={deleteModal.open}
                setOpen={setDeleteModal}
                subText={`Deseja remover a oferta da atividade "${deleteModal.offer?.activityName || ""}"? Esta ação não pode ser desfeita.`}
                handleConfirmation={handleDelete}
            />
        </Container>
    );
};

export default MechanicActivityOffers;
